// Frontend service that communicates with the backend API instead of using the SDK directly

export interface ClaudeCodeConfig {
  projectPath: string;
  model?: string;
  maxTurns?: number;
  allowedTools?: string[];
  permissionMode?: 'default' | 'acceptEdits' | 'bypassPermissions' | 'plan';
}

export interface ChatResponse {
  success: boolean;
  sessionId: string;
  message: string;
  cost?: number;
  duration?: number;
  error?: string;
}

export interface FormattedSDKMessage {
  id: string;
  type: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  subagent?: string;
  slashCommand?: string;
}

export class ClaudeCodeSDKService {
  private config: ClaudeCodeConfig;
  private apiBaseUrl: string;

  constructor(config: ClaudeCodeConfig) {
    this.config = config;
    this.apiBaseUrl = import.meta.env.VITE_API_URL || 'https://byte-devops-ide.square-butterfly-6b3d.workers.dev';
  }

  async sendMessage(content: string, subagent?: string): Promise<FormattedSDKMessage[]> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/claude/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: content,
          options: {
            model: this.config.model || 'claude-3-5-sonnet-20241022',
            maxTurns: this.config.maxTurns || 10,
            allowedTools: this.config.allowedTools || [
              'Read', 'Write', 'Edit', 'MultiEdit', 'Bash', 'Grep', 'Glob', 
              'Task', 'WebSearch', 'WebFetch', 'NotebookEdit', 'TodoWrite'
            ],
            permissionMode: this.config.permissionMode || 'default',
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.statusText}`);
      }

      const result: ChatResponse = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Unknown error occurred');
      }

      // Convert API response to formatted messages
      const userMessage: FormattedSDKMessage = {
        id: `user_${Date.now()}`,
        type: 'user',
        content: content,
        timestamp: new Date(),
        subagent: subagent,
      };

      const assistantMessage: FormattedSDKMessage = {
        id: `assistant_${Date.now()}`,
        type: 'assistant',
        content: result.message,
        timestamp: new Date(),
        subagent: subagent,
      };

      return [userMessage, assistantMessage];
    } catch (error) {
      console.error('Failed to send message:', error);
      
      const errorMessage: FormattedSDKMessage = {
        id: `error_${Date.now()}`,
        type: 'system',
        content: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date(),
      };

      return [errorMessage];
    }
  }

  async getSessions(): Promise<any[]> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/sessions`);
      if (!response.ok) {
        throw new Error(`Failed to fetch sessions: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      return [];
    }
  }

  async createSession(name: string): Promise<any> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create session: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to create session:', error);
      throw error;
    }
  }

  async getProjects(): Promise<any[]> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/projects`);
      if (!response.ok) {
        throw new Error(`Failed to fetch projects: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      return [];
    }
  }

  async getProjectFiles(projectId: string): Promise<any[]> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/projects/${projectId}/files`);
      if (!response.ok) {
        throw new Error(`Failed to fetch project files: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch project files:', error);
      return [];
    }
  }

  // Health check
  async checkHealth(): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/health`);
      return response.ok;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }
}